import 'focus-visible';

import { ErrorBoundary } from '@dynamic/ui';
import { FC, lazy, Suspense } from 'react';
import { useLocation } from 'react-router';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import styled from 'styled-components';

import { useLoginTveComplete } from '#/components/Login/useLoginTve';
import ServiceSelector from '#/dev/ServiceSelector';
import { useConfig, useDataContext, useFeaturesInfo, useMenu } from '#/hooks';
import { useInitAutoLogin } from '#/hooks/useAutoLogin';
// HomePage and PageController are not lazy loaded because they are landing pages. This way is more efficient and performant
import { HomePage } from '#/pages/Home/HomePage';
import PageController from '#/pages/PageController';
import { UnauthenticatedHomePage } from '#/pages/UnauthenticatedHomePage/UnauthenticatedHomePage';
import { useUnauthenticatedHomePage } from '#/pages/UnauthenticatedHomePage/useUnauthenticatedHomePage';
import AdsManager from '#/plugins/ads/AdsManager';
import AnalyticsManager from '#/plugins/analytics/AnalyticsManager';
import PageAdsManager from '#/plugins/pageAds/PageAdsManager';
import Paywall from '#/plugins/paywall/Paywall';
import PaywallManager from '#/plugins/paywall/PaywallManager';
import PrivacyManager from '#/plugins/privacy/PrivacyManager';
import { AppStatus, StorageKeys } from '#/service/constants';
import { GeoRestrictionError } from '#/service/error/errors';
import { Path } from '#/service/routes';
import { useTranslations } from '#/store/translation/translation.store';

import { initApp } from './appInit';
import { ScrollToTop } from './components/common';
import Footer from './components/Footer';
import Header, { HeaderMode } from './components/Header';
import { Login } from './components/Login';
import { GenericModal } from './components/Modal/GenericModal';
import Splash from './components/Splash/Splash';
import { CenteredSpinner } from './components/Splash/styled';
import { Fallback, NotFound, Status } from './components/Status';
import { TermsModal } from './components/Terms';
import { useGlobalContext } from './components/useGlobalContext';
import { useGiftingRoutes } from './pages/Gifting';
import { usePromoRoutes } from './pages/Promos';
import { useAuthentication } from './store/auth';

const DetailsPage = lazy(
  () => import(/* webpackChunkName: "Details" */ '#/pages/Details/DetailsPage')
);
const ProgramGuidePage = lazy(
  () =>
    import(
      /* webpackChunkName: "ProgramGuide" */ '#/pages/ProgramGuide/ProgramGuidePage'
    )
);
const ProgramGuideV2Page = lazy(
  () =>
    import(
      /* webpackChunkName: "ProgramGuideV2" */ '#/pages/ProgramGuideV2/ProgramGuideV2Page'
    )
);
const PlaybackPage = lazy(
  () =>
    import(/* webpackChunkName: "Playback" */ '#/pages/Playback/PlaybackPage')
);
const ReadMePage = lazy(
  () => import(/* webpackChunkName: "ReadMe" */ '#/pages/ReadMe/ReadMePage')
);
const TveSecondScreenAuthPage = lazy(
  () =>
    import(
      /* webpackChunkName: "Tve2ndScreenAuth" */ '#/pages/TveSecondScreenAuth/TveSecondScreenAuth'
    )
);
const SecondScreenAuthPage = lazy(
  () =>
    import(
      /* webpackChunkName: "2ndScreenAuth" */ '#/pages/SecondScreenAuth/SecondScreenAuth'
    )
);
const RegistrationPage = lazy(
  () =>
    import(
      /* webpackChunkName: "Registration" */ '#/pages/Registration/RegistrationPage'
    )
);
const SearchPage = lazy(
  () => import(/* webpackChunkName: "Search" */ '#/pages/Search/SearchPage')
);
const SettingsPage = lazy(
  () =>
    import(/* webpackChunkName: "Settings" */ '#/pages/Settings/SettingsPage')
);
const SubscriptionPage = lazy(
  () =>
    import(
      /* webpackChunkName: "Subscription" */ '#/pages/Subscription/SubscriptionPage'
    )
);
const WatchHistoryPage = lazy(
  () =>
    import(
      /* webpackChunkName: "WatchHistory" */ '#/pages/WatchHistory/WatchHistoryPage'
    )
);
const ForgotPassword = lazy(
  () =>
    import(
      /* webpackChunkName: "ForgotPsw" */ '#/pages/ForgotPassword/ForgotPasswordPage'
    )
);
const FavoritesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "Favorites" */ '#/pages/Favorites/FavoritesPage'
    )
);
const ExternalPaymentPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ExtPayment" */ '#/pages/ExternalPayment/ExternalPaymentPage'
    )
);
const AlternativePurchasePage = lazy(
  () =>
    import(
      /* webpackChunkName: "AlternativePurchase" */ '#/pages/AlternativePurchase/AlternativePurchasePage'
    )
);

const PageContainer = styled.div`
  min-height: 100vh;
`;

initApp();

const devAdsEnabled = localStorage.getItem(StorageKeys.DEVTOOLS_ADS) === 'true';

const shouldStartSplashFadeout = (
  isBooted: boolean,
  isDictionaryReady: boolean,
  isPluginManagerReady: boolean,
  isGeoBlocked: boolean
) => isBooted && isDictionaryReady && (isPluginManagerReady || isGeoBlocked);

const checkAppLoadingState = (
  isBooted: boolean,
  isActive: boolean,
  isDictionaryReady: boolean,
  isGeoBlocked: boolean
) => {
  if (isBooted && (!isActive || !isDictionaryReady) && !isGeoBlocked) {
    return AppStatus.Loading;
  }

  if (isBooted && isActive && isDictionaryReady && !isGeoBlocked) {
    return AppStatus.Active;
  }
};

const getStatusMessage = (status: AppStatus, keyStatusMessage: string) =>
  status === AppStatus.Active ? keyStatusMessage : '';

const App: FC = () => {
  const globalContext = useGlobalContext();
  const location = useLocation();
  const { getGeneralContext } = useDataContext();
  const {
    isLoggedIn,
    isEntitled,
    isRegistrationEnabled,
    isForgotPasswordEnabled,
    isSecondScreen,
    isTve,
  } = useAuthentication();

  useLoginTveComplete();
  useTranslations();

  const { boot } = globalContext;
  const {
    app,
    footer,
    restrictions: { geoblocked: isGeoBlocked = false } = {},
  } = useConfig();

  const { shouldShow: shouldShowUnauthenticatedHomePage } =
    useUnauthenticatedHomePage();
  const headerMode = shouldShowUnauthenticatedHomePage
    ? HeaderMode.Reduced
    : HeaderMode.Default;

  // TODO#configStore?: own hook for core config features enabled flags
  const { isFeatureEnabled, isProgramGuideV2Enabled } = useFeaturesInfo();
  const promoRoutes = usePromoRoutes();
  const giftingRoutes = useGiftingRoutes();
  const menu = useMenu();
  const { isBooted, isActive, isDictionaryReady, isPluginManagerReady } = boot;
  const { status, keyStatusMessage } = app;
  const statusMessage = getStatusMessage(status, keyStatusMessage);
  const defaultContext = getGeneralContext();

  // We have to re-render the error boundary to dismiss the error. Otherwise, if you run into a "login required"
  // error, it won't disappear even after you logged in. Errors should also be dismissed when navigating back (or away).
  // Add more terms into the key as needed.
  const errorBoundaryRerenderKey = [
    'error',
    isLoggedIn,
    isEntitled,
    location.pathname,
  ].join(',');

  const startSplashFadeout = shouldStartSplashFadeout(
    isBooted,
    isDictionaryReady,
    isPluginManagerReady,
    isGeoBlocked
  );

  const appLoadingState = checkAppLoadingState(
    isBooted,
    isActive,
    isDictionaryReady,
    isGeoBlocked
  );

  useInitAutoLogin();

  return (
    <>
      <ScrollToTop />
      {isGeoBlocked && <Fallback error={new GeoRestrictionError()} />}
      {appLoadingState === AppStatus.Loading && (
        <Status status={status} statusMessage={statusMessage} />
      )}
      {appLoadingState === AppStatus.Active && (
        <>
          {/* Main error boundary for the rest of the app */}
          <ErrorBoundary
            FallbackComponent={Fallback}
            rerenderKey={errorBoundaryRerenderKey}
          >
            <>
              <PageContainer>
                <Header cacheKey="app-header" menu={menu} mode={headerMode} />
                <Suspense fallback={<CenteredSpinner />}>
                  <Routes>
                    {isRegistrationEnabled && (
                      <Route
                        element={<RegistrationPage />}
                        path={Path.Registration}
                      />
                    )}
                    {isForgotPasswordEnabled && (
                      <>
                        <Route
                          element={<ForgotPassword />}
                          path={Path.ForgotPassword}
                        />

                        <Route
                          element={<ForgotPassword skipStep />}
                          path={Path.UpdatePassword}
                        />
                      </>
                    )}
                    {isFeatureEnabled('schedule') && (
                      <Route
                        element={
                          isProgramGuideV2Enabled() ? (
                            <ProgramGuideV2Page />
                          ) : (
                            <ProgramGuidePage />
                          )
                        }
                        path={Path.Channels}
                      />
                    )}
                    <Route element={<ReadMePage />} path={Path.Readme} />
                    {isTve && isSecondScreen && (
                      <Route
                        element={<TveSecondScreenAuthPage />}
                        path={Path.TveSecondScreenLogin}
                      />
                    )}
                    {isSecondScreen && !isTve && (
                      <Route
                        element={<SecondScreenAuthPage />}
                        path={Path.SecondScreenLogin}
                      />
                    )}
                    <Route element={<SettingsPage />} path={Path.Settings} />
                    <Route element={<SearchPage />} path={Path.Search} />
                    <Route element={<WatchHistoryPage />} path={Path.History} />
                    <Route element={<FavoritesPage />} path={Path.Favorites} />
                    <Route
                      element={<SubscriptionPage />}
                      path={Path.Subscribe}
                    />
                    <Route
                      element={
                        <PageController defaultContext={defaultContext} />
                      }
                      path={Path.Page}
                    />
                    <Route
                      element={<DetailsPage key={location.pathname} />}
                      path={Path.Details}
                    />
                    <Route
                      /**
                       * If you were wondering: `Hold on, what the heck is this?`, then let me explain what's going on here
                       * Playback page for some reason is slightly strange and changing resource between playbacks
                       * does not recreate component fully. Setting `key` to the pathname (which contains resourceId in this case)
                       * enforces component to recreate between plays.
                       * If you remove the `key` attribute, please test UpNext functionality
                       */
                      element={<PlaybackPage key={location.pathname} />}
                      path={Path.Playback}
                    />
                    <Route
                      element={<ExternalPaymentPage />}
                      path={Path.External}
                    />
                    <Route
                      element={<AlternativePurchasePage />}
                      path={Path.AlternativePurchase}
                    />
                    <Route
                      element={
                        shouldShowUnauthenticatedHomePage ? (
                          <UnauthenticatedHomePage />
                        ) : (
                          <HomePage />
                        )
                      }
                      path={Path.Home}
                    />

                    {/* Gifting */}
                    {giftingRoutes}

                    {/* Promo Campaigns */}
                    {promoRoutes}

                    {/* everything else */}
                    <Route element={<NotFound />} path="*" />
                  </Routes>
                </Suspense>
                {process.env.goal === 'local' && <ServiceSelector />}
              </PageContainer>
              <Footer footer={footer} />
            </>
          </ErrorBoundary>
          {/* Modals are in a separate boundary, so even if the rest of the app fails and renders Fallback, they stay in the DOM. */}
          <ErrorBoundary FallbackComponent={Fallback}>
            <>
              <Login />
              <TermsModal isAppReady={isBooted && isDictionaryReady} />
              <GenericModal />
            </>
          </ErrorBoundary>
        </>
      )}
      <AnalyticsManager />
      <PaywallManager />
      <Paywall />
      {(process.env.goal !== 'local' || devAdsEnabled) && (
        <>
          <AdsManager />
          <PageAdsManager />
        </>
      )}
      <PrivacyManager />
      <Splash menu={menu} startFadeout={startSplashFadeout} />
    </>
  );
};

const AppRouter = () => (
  <Router>
    <App />
  </Router>
);

export default AppRouter;
